import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import Workflow from 'models/Workflow';
import {
  completeWorkflowAction,
  showWorkflow,
  workflowQueryKeys,
  WorkflowValuesInput,
  writeWorkflowValues,
} from 'services/api/workflows';
import { useToastActions } from 'stores/toastStore';

// Every write (create / values / action complete) commits and enqueues the
// evaluator ASYNCHRONOUSLY — the HTTP response reflects PRE-evaluation state, and
// the derived effects (which actions flip to action_required, whether the run
// completes) land a beat later. So Phase 0 observes settled state by polling.
const POLL_INTERVAL_MS = 2000;

/**
 * The single data hook for a workflow run (the key WEB-0 architectural ask).
 *
 * All fetching lives here so the rest of the UI is agnostic to *how* fresh data
 * arrives. Today it polls `GET /api/workflows/:id` until the run is terminal;
 * swapping to a WebSocket subscription in Phase 2 is a one-file change — only the
 * `useQuery` block below changes, the returned shape stays the same.
 */
export default function useWorkflow(id: string | undefined) {
  const queryClient = useQueryClient();
  const { addToast } = useToastActions();

  const query = useQuery({
    queryKey: workflowQueryKeys.show(id ?? ''),
    queryFn: ({ signal }) => showWorkflow(id!, signal),
    enabled: !!id,
    // --- swap point: replace this polling with a subscription in Phase 2 ---
    // Poll while the run is still live; stop once it terminalizes (design §11).
    refetchInterval: (q) => (q.state.data && q.state.data.isTerminal ? false : POLL_INTERVAL_MS),
    refetchIntervalInBackground: true,
  });

  // Seed the cache with a write's response so the action's own synchronous flip
  // shows immediately; the poll then settles the downstream/derived effects.
  const onWriteSuccess = (workflow: Workflow) => {
    if (id) queryClient.setQueryData(workflowQueryKeys.show(id), workflow);
  };

  const onWriteError = () => addToast({ text: 'Something went wrong. Please try again' });

  const valuesMutation = useMutation({
    mutationFn: (values: WorkflowValuesInput) => writeWorkflowValues({ workflowId: id!, values }),
    onSuccess: onWriteSuccess,
    onError: onWriteError,
  });

  const actionMutation = useMutation({
    mutationFn: ({ actionId, outputs }: { actionId: string; outputs: WorkflowValuesInput }) =>
      completeWorkflowAction({ workflowId: id!, actionId, outputs }),
    onSuccess: onWriteSuccess,
    onError: onWriteError,
  });

  return {
    workflow: query.data,
    isLoading: query.isPending && !!id,
    isError: query.isError,
    // True while a poll or write is in flight — drives a subtle "updating" affordance.
    isFetching: query.isFetching,
    refetch: query.refetch,
    writeValues: valuesMutation.mutate,
    isWritingValues: valuesMutation.isPending,
    completeAction: actionMutation.mutate,
    isCompletingAction: actionMutation.isPending,
  };
}
